import React, { FC, useState } from 'react'

import Modal from 'uiKit/Modal'
import { USAGE_METRIC_META } from 'constants/usageLimits'
import { requestLimitIncrease } from 'tabs/home/api/usageLimits'
import { getCachedUsageLimitRange } from 'tabs/home/helpers/packageTiers'

import * as S from './UsageMeter.style'

interface Props {
  open: boolean
  onClose: () => void
  botId: string | number
  botName?: string
  requestedBy?: string
  metric: string
  currentLimit: number | null
}

/**
 * The customer's ask for more allowance.
 *
 * Granting it stays a manual decision, so this collects only what a CareBrain
 * admin needs to make it: which metric, what number would be enough, and why.
 * The "why" is optional — a required justification field turns a 10-second ask
 * into a chore and people work around it by emailing instead.
 */
export const RequestLimitIncreaseModal: FC<Props> = ({
  open,
  onClose,
  botId,
  botName,
  requestedBy,
  metric,
  currentLimit,
}) => {
  const meta = USAGE_METRIC_META[metric]
  const { min: limitMin, max: limitMax } = getCachedUsageLimitRange()
  const suggested = currentLimit ? String(Math.min(currentLimit * 2, limitMax)) : ''
  const limitNoun = currentLimit === 1 ? meta?.noun : meta?.nounPlural
  const currentCopy =
    currentLimit === null
      ? 'This organisation currently has no monthly limit.'
      : `Your organisation is currently allowed ${currentLimit} ${limitNoun} per month.`

  const [requestedLimit, setRequestedLimit] = useState<string>(suggested)
  const [reason, setReason] = useState('')
  const [sending, setSending] = useState(false)

  const limitNum = Number(requestedLimit)
  const isOutOfRange =
    requestedLimit !== '' && (Number.isNaN(limitNum) || limitNum < limitMin || limitNum > limitMax)
  const isLimitInvalid = requestedLimit === '' || isOutOfRange
  const showLimitError = isOutOfRange

  const handleSend = () => {
    if (isLimitInvalid) return
    setSending(true)
    requestLimitIncrease(botId, {
      metric,
      requestedLimit: limitNum,
      reason: reason.trim() || undefined,
      botName,
      requestedBy,
    })
      .then(() => onClose())
      .finally(() => setSending(false))
  }

  return (
    <Modal open={open} onClose={onClose} tabIndex="-1">
      <S.ModalBody>
        <S.ModalTitle>Request a higher {meta?.label.toLowerCase() || 'usage'} limit</S.ModalTitle>
        <S.ModalText>
          {currentCopy} Tell us what you need and someone will confirm the change with you.
        </S.ModalText>

        <S.FieldLabel>
          New monthly limit
          <S.NumberInput
            type="text"
            inputMode="numeric"
            maxLength={String(limitMax).length}
            value={requestedLimit}
            aria-invalid={showLimitError}
            onChange={event => setRequestedLimit(event.target.value.replace(/[^0-9]/g, ''))}
          />
          {showLimitError && (
            <S.FieldError>
              Monthly limit must be between {limitMin} and {limitMax}.
            </S.FieldError>
          )}
        </S.FieldLabel>

        <S.FieldLabel>
          Anything we should know? (optional)
          <S.TextArea
            value={reason}
            placeholder="e.g. we are onboarding two new care homes in October"
            onChange={event => setReason(event.target.value)}
          />
        </S.FieldLabel>

        <S.ModalActions>
          <S.GhostButton onClick={onClose}>Cancel</S.GhostButton>
          <S.PrimaryButton disabled={sending || isLimitInvalid} onClick={handleSend}>
            {sending ? 'Sending…' : 'Send request'}
          </S.PrimaryButton>
        </S.ModalActions>
      </S.ModalBody>
    </Modal>
  )
}

export default RequestLimitIncreaseModal
